import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getGradeInfo } from '../utils/gradeDisplay';
import { translateLeague } from '../utils/leagueNames';
import { buildAccaLegs, slipSummary, matchTicketLegs, ticketSummary } from '../utils/acca';

// Honest out-of-sample pool hit rates (grading_v4_validation.py, clean replay
// TEST split 2024-25, variable-K acca legs in the 1.70-3.00 odds band --
// exactly the acca_candidate pool this builder draws from). The previous
// 78.3%/70.6% figures came from the contaminated pre-clean backtest.
const POOL_HIT_RATE = {
  btts: 0.650,
  over25: 0.622,
  // draw: variable-K acca legs, same validation run (34.8% at odds >= 3.0).
  // outcome (outsiders 3.5-6.0): no honest pooled rate exists yet -> null hides
  // the per-leg rate line and the slip's estimated hit probability.
  draw: 0.348,
  outcome: null,
};

// Accas are per-system products (SYSTEM_OF in utils/acca): goals legs
// (btts+over25) mix on one slip, the 6-leg draw system and the outsiders system
// are separate slips. Selecting a leg locks the slip to that system.
const SYSTEM_PROVISIONAL = { goals: false, draw: true, outsiders: true };

function getLocale(lang) {
  const map = { en: 'en-GB', es: 'es-ES', el: 'el-GR', de: 'de-DE', it: 'it-IT', tr: 'tr-TR' };
  return map[lang] || 'en-GB';
}

function formatKickoff(dateTime, lang) {
  if (!dateTime || dateTime === 'TBD') return 'TBD';
  const locale = getLocale(lang);
  try {
    if (dateTime.includes(' - ')) {
      const [datePart, timePart] = dateTime.split(' - ');
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(am|pm)/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2];
        const period = timeMatch[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        const dateObj = new Date(`${datePart} ${formattedTime} GMT`);
        if (!isNaN(dateObj.getTime())) {
          const d = dateObj.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
          const tm = dateObj.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
          return `${d} ${tm}`;
        }
      }
      return dateTime;
    }
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      const d = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      const tm = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return `${d} ${tm}`;
    }
    return dateTime;
  } catch {
    return dateTime;
  }
}


const SCORE_COLOR = {
  live: 'text-accent',
  won: 'text-win',
  lost: 'text-loss',
  finished: 'text-gray-300',
};

function hasScore(leg) {
  return leg.result != null && leg.result.homeGoals != null && leg.result.awayGoals != null
    && (leg.status === 'live' || leg.status === 'won' || leg.status === 'lost' || leg.status === 'finished');
}

/** Live minute / WIN / LOSS / VOID / FT chip; null while the leg is still open. */
function LegStatusChip({ leg, t }) {
  if (leg.status === 'live') {
    const r = leg.result || {};
    const minute = r.status === 'HALF_TIME' ? 'H/T'
      : r.matchTime ? (/^\d+$/.test(r.matchTime) ? `${r.matchTime}'` : r.matchTime)
      : r.elapsedMinutes ? `${r.elapsedMinutes}'` : t('live');
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent/20 rounded text-[10px] font-medium text-accent whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
        {minute}
      </span>
    );
  }
  const chip = {
    won: ['bg-win/15 text-win', t('win')],
    lost: ['bg-loss/15 text-loss', t('loss')],
    void: ['bg-yellow-500/15 text-yellow-500', t('accaVoid')],
    finished: ['bg-anthracite-800 text-gray-400', 'FT'],
    started: ['bg-anthracite-800 text-gray-400', t('accaStarted')],
  }[leg.status];
  if (!chip) return null;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${chip[0]}`}>{chip[1]}</span>
  );
}

/** "3/4 won" tally chip for a group of legs (suggested ticket or the user's slip). */
function TallyChip({ legs, t }) {
  const s = ticketSummary(legs);
  if (s.settled === 0 && !legs.some(l => l.status === 'live')) return null;
  const tone = s.state === 'won' ? 'bg-win/15 text-win border-win/30'
    : s.state === 'lost' ? 'bg-loss/15 text-loss border-loss/30'
    : 'bg-anthracite-800 text-gray-300 border-anthracite-600';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold whitespace-nowrap ${tone}`}>
      {t('accaTally', { won: s.won, total: s.total })}
    </span>
  );
}

/**
 * Acca Builder — lists every backtested acca candidate (goals, draw and
 * outsiders systems) with live/final results, plus the suggested mixed ticket,
 * and lets the owner assemble/copy an accumulator slip.
 *
 * `selected`/`setSelected` are owned by the Dashboard (so selections survive tab
 * switches and reloads); a local fallback keeps the component usable on its own.
 */
function AccaBuilder({ predictions, results, selected: selectedProp, setSelected: setSelectedProp, now }) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);

  const MARKET_LABEL = {
    btts: t('btts'),
    over25: t('accaMarketOver25'),
    draw: t('accaMarketDraw'),
    outcome: t('accaMarketOutsider'),
  };
  const SYSTEM_LABEL = {
    goals: t('accaSystemGoals'),
    draw: t('accaSystemDraw'),
    outsiders: t('accaSystemOutsiders'),
  };

  const [localSelected, setLocalSelected] = useState({});
  const selected = selectedProp || localSelected;
  const setSelected = setSelectedProp || setLocalSelected;
  const [stake, setStake] = useState(10);
  const [copied, setCopied] = useState(false);

  const legs = useMemo(() => buildAccaLegs(predictions, results, now), [predictions, results, now]);
  const openCount = legs.filter(l => l.status === 'open').length;

  const selectedLegs = useMemo(() => legs.filter(leg => selected[leg.key]), [legs, selected]);
  const activeSystem = selectedLegs.length > 0 ? selectedLegs[0].system : null;

  const { activeLegs, voidCount, combinedOdds } = slipSummary(selectedLegs);
  // Only claim an estimated hit probability when every counted leg has an
  // honest backtested pool rate (outsiders have none).
  const estimatedHitProb = activeLegs.length > 0 && activeLegs.every(leg => POOL_HIT_RATE[leg.market] != null)
    ? activeLegs.reduce((acc, leg) => acc * POOL_HIT_RATE[leg.market], 1)
    : null;
  const potentialReturn = stake > 0 && activeLegs.length > 0 ? stake * combinedOdds : 0;
  const legCount = selectedLegs.length;
  const longAccaWarning = legCount > 8;

  const mixedTicket = Array.isArray(predictions.acca) && predictions.acca.length > 0
    ? predictions.acca[0] : null;
  const ticketLegs = useMemo(
    () => matchTicketLegs(mixedTicket, predictions, results),
    [mixedTicket, predictions, results]
  );

  const isSelectable = (leg) =>
    leg.status === 'open' && (activeSystem == null || leg.system === activeSystem);

  const toggleLeg = (leg) => {
    if (selected[leg.key]) {
      // Deselect is always allowed, even once the leg has kicked off.
      setSelected(prev => {
        const next = { ...prev };
        delete next[leg.key];
        return next;
      });
      return;
    }
    if (!isSelectable(leg)) return; // kicked off / void, or slip locked to another system
    setSelected(prev => ({ ...prev, [leg.key]: true }));
  };

  const marketText = (leg) => (leg.market === 'outcome' && leg.bet_label ? leg.bet_label : MARKET_LABEL[leg.market]);

  const handleCopySlip = async () => {
    const lines = selectedLegs.map(leg =>
      `${leg.home_team} ${t('common:vs')} ${leg.away_team} — ${marketText(leg)} @ ${(leg.odds || 0).toFixed(2)}${leg.status === 'void' ? ` (${t('accaVoid')})` : ''}`
    );
    const text = [
      t('accaSlipTitle', { count: legCount }),
      ...lines,
      t('accaCombinedOddsLine', { odds: combinedOdds.toFixed(2) }),
      t('accaStakeReturnLine', { stake, return: potentialReturn.toFixed(2) }),
      ...(estimatedHitProb != null
        ? [t('accaSlipHitProbLine', { pct: (estimatedHitProb * 100).toFixed(1) })]
        : []),
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — fail silently, button will just not confirm
    }
  };

  if (legs.length === 0 && ticketLegs.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-anthracite-800 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m-6 4h6m-6 4h4M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
          </svg>
        </div>
        <div className="text-gray-400 text-lg mb-2">{t('accaEmptyTitle')}</div>
        <p className="text-gray-600 text-sm max-w-sm mx-auto">
          {t('accaEmptyDesc')}
        </p>
      </div>
    );
  }

  // Room for the fixed mobile slip (taller with the long-acca / void notes).
  const slipExtras = (longAccaWarning ? 1 : 0) + (voidCount > 0 ? 1 : 0);
  const bottomPad = legCount === 0 ? '' : ['pb-52 md:pb-8', 'pb-64 md:pb-8', 'pb-72 md:pb-8'][slipExtras];

  return (
    <div className={bottomPad}>
      {ticketLegs.length > 0 && (
        <div className="mb-6 rounded-xl border border-anthracite-700 bg-anthracite-900 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-3 md:px-4 py-2.5 bg-anthracite-800 border-b border-anthracite-700">
            <span className="w-full sm:w-auto sm:flex-1 min-w-0 text-sm font-semibold text-white">{t('accaMixedTitle', { count: mixedTicket.n_legs || ticketLegs.length })}</span>
            <TallyChip legs={ticketLegs} t={t} />
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 uppercase tracking-wide">{t('accaMixedBreakEven')}</span>
          </div>
          <div className="px-3 md:px-4 py-2">
            {ticketLegs.map((l, i) => (
              <div key={l.id || i} className="flex items-center gap-2 py-1.5 text-sm border-b border-anthracite-800 last:border-0">
                <span className="hidden md:block text-[10px] w-12 flex-shrink-0 uppercase text-gray-500 truncate">{MARKET_LABEL[l.market] || l.market}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-white truncate">{l.home} <span className="text-gray-500 text-xs">{t('common:vs')}</span> {l.away}</div>
                  <div className="md:hidden text-[10px] uppercase text-gray-500 truncate">{MARKET_LABEL[l.market] || l.market}</div>
                </div>
                {hasScore(l) && (
                  <span className={`stat-number flex-shrink-0 text-xs font-bold ${SCORE_COLOR[l.status]}`}>{l.result.homeGoals}-{l.result.awayGoals}</span>
                )}
                <span className="flex-shrink-0"><LegStatusChip leg={l} t={t} /></span>
                <span className={`stat-number flex-shrink-0 text-gray-300 ${l.status === 'void' ? 'line-through text-gray-500' : ''}`}>{(l.odds || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 md:px-4 py-2 bg-anthracite-800/60 text-[11px] text-gray-400">
            <span className="min-w-0">{t('accaMixedCover', { k: mixedTicket.cover_system?.k, n: mixedTicket.cover_system?.n, combos: mixedTicket.cover_system?.combinations })}</span>
            <span className="stat-number ml-auto text-accent font-semibold">{(mixedTicket.combined_odds || 0).toFixed(2)}x</span>
          </div>
        </div>
      )}

      {legs.length > 0 && (
        <>
      <div className="mb-4 text-sm text-gray-500">
        {openCount > 0 ? t('accaAvailable', { count: openCount }) : t('accaNoneOpen')}
      </div>

      <div className="prediction-list rounded-xl border border-anthracite-700 overflow-hidden bg-anthracite-900">
        {/* Header row - Desktop only */}
        <div className="hidden md:flex items-center px-4 py-2 gap-4 bg-anthracite-800 border-b border-anthracite-700 text-xs text-gray-500 uppercase tracking-wider">
          <div className="w-6 flex-shrink-0"></div>
          <div className="w-24 flex-shrink-0">{t('accaKickoff')}</div>
          <div className="flex-1 min-w-0">{t('match')}</div>
          <div className="w-24 text-center flex-shrink-0">{t('accaMarketHeader')}</div>
          <div className="w-[52px] text-center flex-shrink-0">{t('grade')}</div>
          <div className="w-[52px] text-center flex-shrink-0">{t('odds')}</div>
        </div>

        {legs.map((leg, idx) => {
          const isChecked = !!selected[leg.key];
          const grade = getGradeInfo(leg.composite_grade, leg.market, leg.grade_letter);
          const isNewSystem = idx === 0 || legs[idx - 1].system !== leg.system;
          const isLocked = leg.status === 'open' && activeSystem != null && leg.system !== activeSystem;
          const canToggle = isChecked || isSelectable(leg);
          const kickoff = formatKickoff(leg.date_time, i18n.language);
          const league = translateLeague(leg.league, i18n.language);
          const score = hasScore(leg);
          const scoreColor = SCORE_COLOR[leg.status] || 'text-gray-300';
          const hitRate = POOL_HIT_RATE[leg.market] != null
            ? t('accaPoolHitRate', { rate: Math.round(POOL_HIT_RATE[leg.market] * 100) })
            : t('accaNoProvenRate');
          const chip = <LegStatusChip leg={leg} t={t} />;
          const isOpen = leg.status === 'open';
          return (
            <React.Fragment key={leg.key}>
            {isNewSystem && (
              <div className="flex flex-wrap items-center gap-2 px-3 md:px-4 py-2 bg-anthracite-800/70 border-b border-anthracite-700">
                <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">{SYSTEM_LABEL[leg.system]}</span>
                {SYSTEM_PROVISIONAL[leg.system] && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/15 border border-yellow-500/30 text-yellow-400 uppercase tracking-wide">{t('accaProvisional')}</span>
                )}
              </div>
            )}
            <div
              onClick={() => toggleLeg(leg)}
              className={`flex items-center px-3 md:px-4 py-3 gap-3 md:gap-4 border-b border-anthracite-700 transition-colors ${
                isLocked ? 'opacity-40 cursor-not-allowed'
                : isChecked ? 'bg-accent/5 cursor-pointer'
                : canToggle ? 'hover:bg-anthracite-800/50 cursor-pointer'
                : 'cursor-default'
              }`}
            >
              <div className="w-6 flex-shrink-0 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={!canToggle}
                  onChange={() => toggleLeg(leg)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-anthracite-600 text-accent focus:ring-accent bg-anthracite-800 disabled:opacity-40"
                />
              </div>
              {/* Kickoff / status - desktop column */}
              <div className="hidden md:block w-24 flex-shrink-0 text-[11px] text-gray-500 whitespace-nowrap">
                <div>{kickoff}</div>
                {!isOpen && <div className="mt-1">{chip}</div>}
              </div>
              <div className="flex-1 min-w-0">
                {/* Desktop: one line, score replaces "vs" once the match is on */}
                <div className="hidden md:flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-white truncate">{leg.home_team}</span>
                  {score ? (
                    <span className={`stat-number flex-shrink-0 text-sm font-bold ${scoreColor}`}>{leg.result.homeGoals} - {leg.result.awayGoals}</span>
                  ) : (
                    <span className="flex-shrink-0 text-xs text-gray-500">{t('common:vs')}</span>
                  )}
                  <span className="text-sm font-medium text-white truncate">{leg.away_team}</span>
                </div>
                <div className="hidden md:block text-xs text-gray-500 mt-0.5 truncate">{league}</div>
                {/* Mobile: stacked teams with inline score, then status/kickoff, league and market */}
                <div className="md:hidden">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-white truncate">{leg.home_team}</span>
                    {score && <span className={`text-[13px] font-bold flex-shrink-0 ${scoreColor}`}>{leg.result.homeGoals}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-white truncate">{leg.away_team}</span>
                    {score && <span className={`text-[13px] font-bold flex-shrink-0 ${scoreColor}`}>{leg.result.awayGoals}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 min-w-0 text-[10px] text-gray-500">
                    {isOpen ? <span className="flex-shrink-0 whitespace-nowrap">{kickoff}</span> : <span className="flex-shrink-0">{chip}</span>}
                    <span className="flex-shrink-0">·</span>
                    <span className="truncate">{league}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] truncate">
                    <span className="font-semibold text-gray-300">{marketText(leg)}</span>
                    <span className="text-gray-500"> · {hitRate}</span>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-24 flex-shrink-0 text-center">
                <div className="text-xs font-semibold text-gray-300 truncate">{marketText(leg)}</div>
                <div className="text-[9px] text-gray-500 mt-0.5">{hitRate}</div>
              </div>
              <div className={`w-11 md:w-[52px] flex-shrink-0 rounded px-1 md:px-2.5 py-1 text-center ${grade.bg}`}>
                <div className="text-[9px] text-gray-500 uppercase tracking-wide truncate">{t('grade')}</div>
                <div className={`stat-number text-sm font-bold ${grade.color}`}>{grade.letter}</div>
              </div>
              <div className="w-11 md:w-[52px] flex-shrink-0 text-center">
                <div className="text-[9px] text-gray-500 uppercase tracking-wide truncate">{t('odds')}</div>
                <div className={`stat-number text-sm ${leg.status === 'void' ? 'text-gray-500 line-through' : 'text-gray-300'}`}>{(leg.odds || 0).toFixed(2)}</div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>
        </>
      )}

      {/* Summary slip — bottom bar on mobile, floating panel on desktop */}
      {legCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:left-auto md:right-6 md:bottom-6 md:w-80 bg-anthracite-900 border-t md:border md:rounded-xl border-anthracite-700 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:p-4 md:shadow-glow">
          <div className="flex items-center gap-2 mb-2">
            <span className="min-w-0 flex-1 text-sm font-semibold text-white truncate">{t('accaLegsSelected', { count: legCount })}</span>
            <TallyChip legs={selectedLegs} t={t} />
            <span className="stat-number text-sm text-accent font-bold flex-shrink-0">{combinedOdds.toFixed(2)}x</span>
          </div>

          {voidCount > 0 && (
            <div className="text-[11px] text-gray-500 mb-2">{t('accaVoidExcluded', { count: voidCount })}</div>
          )}

          {longAccaWarning && (
            <div className="mb-2 px-2 py-1.5 rounded bg-yellow-500/10 border border-yellow-500/30 text-[11px] text-yellow-400">
              {t('accaLongWarning', { count: legCount })}
            </div>
          )}

          <div className="flex items-center gap-3 mb-2">
            <label className="text-xs text-gray-500 flex-shrink-0">{t('accaStakeLabel')}</label>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="decimal"
              value={stake}
              onChange={(e) => setStake(parseFloat(e.target.value) || 0)}
              className="w-20 bg-anthracite-800 border border-anthracite-700 rounded px-2 py-1 text-base md:text-sm text-white focus:outline-none focus:border-accent"
            />
            <div className="ml-auto text-right">
              <div className="text-[9px] text-gray-500 uppercase tracking-wide">{t('accaReturnLabel')}</div>
              <div className="stat-number text-sm font-semibold text-win">€{potentialReturn.toFixed(2)}</div>
            </div>
          </div>

          {estimatedHitProb != null && (
            <div className="text-[11px] text-gray-500 mb-3">
              {t('accaHitProbLabel')} <span className="text-gray-300 font-medium">{(estimatedHitProb * 100).toFixed(1)}%</span>
            </div>
          )}

          <button
            onClick={handleCopySlip}
            className="w-full py-2 rounded-lg bg-accent hover:bg-accent-dark text-white text-sm font-medium transition-colors"
          >
            {copied ? t('accaCopied') : t('accaCopySlip')}
          </button>
        </div>
      )}
    </div>
  );
}

export default AccaBuilder;
